// @ts-nocheck
// Base API client — sends credentials (httpOnly cookies) with every request
// Includes 401 → token-refresh retry logic

import { PLATFORM_AUTH } from '../api-endpoints/api-endpoints';

const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
  'X-Requested-With': 'XMLHttpRequest',
};
let _refreshing: Promise<boolean> | null = null;

async function doRefresh(): Promise<boolean> {
  try {
    const res = await fetch(PLATFORM_AUTH.refresh, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    return res.ok;
  } catch {
    return false;
  }
}

async function request<T>(url: string, options: RequestInit = {}, retry = true): Promise<T> {
  const res = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: { ...DEFAULT_HEADERS, ...(options.headers || {}) },
  });

  // 401 → try token refresh once then retry.
  // EXCEPT for auth endpoints (login / signup / refresh / forgot / reset /
  // verify-email / accept-invitation): a 401 there is a credential/validation
  // error, NOT an expired session — running the refresh dance would emit a
  // bogus /auth/refresh call and replace the real backend message ("Invalid
  // email or password") with a generic "Session expired". Let those throw
  // straight through so the screen can show the real error.
  const isAuthEndpoint = /\/v1\/auth\//.test(url);
  if (res.status === 401 && retry && !isAuthEndpoint) {
    if (!_refreshing) {
      _refreshing = doRefresh().finally(() => { _refreshing = null; });
    }
    const ok = await _refreshing;
    if (ok) return request<T>(url, options, false); // retry once

    // Refresh failed → notify auth layer
    if (typeof window !== 'undefined') {
      (window as any).__authExpired = true;
      window.dispatchEvent(new CustomEvent('auth:expired'));
      window.dispatchEvent(new CustomEvent('auth:logout'));
    }
    const err: any = new Error('Session expired');
    err.response = { status: 401, data: { message: 'Session expired. Please log in again.' } };
    throw err;
  }

  if (!res.ok) {
    let errorBody: unknown;
    try { errorBody = await res.json(); } catch { errorBody = { message: res.statusText }; }
    const err: any = new Error('API error');
    err.response = { status: res.status, data: errorBody };
    throw err;
  }

  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
}

export const apiClient = {
  get: <T>(url: string, headers?: Record<string, string>) =>
    request<T>(url, { method: 'GET', headers }),

  post: <T>(url: string, body?: unknown, headers?: Record<string, string>) =>
    request<T>(url, { method: 'POST', body: body != null ? JSON.stringify(body) : undefined, headers }),

  patch: <T>(url: string, body?: unknown, headers?: Record<string, string>) =>
    request<T>(url, { method: 'PATCH', body: body != null ? JSON.stringify(body) : undefined, headers }),

  put: <T>(url: string, body?: unknown, headers?: Record<string, string>) =>
    request<T>(url, { method: 'PUT', body: body != null ? JSON.stringify(body) : undefined, headers }),

  delete: <T>(url: string, headers?: Record<string, string>) =>
    request<T>(url, { method: 'DELETE', headers }),
};
